import { useForm, useFieldArray, SubmitHandler } from "react-hook-form";
import { Box, Button, Grid, TextField, Typography, Divider, Alert, CircularProgress } from "@mui/material";
import { DateTime } from "luxon";
import { postFormPorting } from "../actions/postFormPorting.actions";
import { previewPortingPDF } from "../actions/previewPortingPDF.actions";
import { useAppSelector } from "../../../../redux/hooks/redux";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { IRoles } from "../../../../permissions/types/IRoles";

interface Telephone {
  number: string;
  billingNumber: string;
  accountPin: string;
}

interface FormValues {
  telephones: Telephone[];
  provider: string;
  customerName: string;
  address: string;
  cityStateZip: string;
  contactPhone: string;
  signatoryName: string;
  title: string;
  signature: string;
  dateSigned: string;
  requestedDeliveryDate?: string;
  requestedDeliveryTime?: string;
}

export const PortingFormStepper = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const { company } = useAppSelector((state) => state.userState);
  const queryClient = useQueryClient();

  const role = company?.role;

  const { control, register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      telephones: [{ number: "", billingNumber: "", accountPin: "" }],
      provider: "",
      customerName: "",
      address: "",
      cityStateZip: "",
      contactPhone: "",
      signatoryName: "",
      title: "",
      signature: "",
      dateSigned: "",
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "telephones" });

  const buildPayload = (data: FormValues) => {
    const now = DateTime.now().setZone("America/Mexico_City");
    return {
      ...data,
      requestedDeliveryDate: now.toISODate(),
      requestedDeliveryTime: now.toFormat("HH:mm:ss"),
      companyId: String(company.id),
      createdBy: role.type,
    };
  };

  const handleGeneratePreview = async (data: FormValues) => {
    const payload = buildPayload(data);
    try {
      setLoading(true);
      const blob = await previewPortingPDF(payload);
      const objectUrl = URL.createObjectURL(blob);
      setPdfUrl(objectUrl);
      setActiveStep(1);
    } catch (error) {
      console.error("Error generating preview:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRecord: SubmitHandler<FormValues> = async (data) => {
    const payload = buildPayload(data);
    try {
      setLoading(true);
      const newRecord = await postFormPorting(payload);
      queryClient.setQueryData(["porting-record", company.id], newRecord);
      setActiveStep(2);
    } catch (error) {
      console.error("Error saving porting:", error);
    } finally {
      setLoading(false);
    }
  };

  const isMaxLimit = fields.length >= 10;

  return (
    <Box
      sx={{
        overflowX: "hidden",
        maxWidth: "100%",
        mx: "auto",
        px: 3,
        m: 1,
        borderRadius: 2,
      }}
    >
      {activeStep === 0 && (
        <Box component="form" onSubmit={handleSubmit(handleGeneratePreview)} sx={{ mt: 4 }}>
          <Typography variant="h5" mb={2} fontWeight="bold">
            Service Change Request
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle1" mb={2}>
            Telephone Information
          </Typography>

          {fields?.map((item, index) => (
            <Grid container spacing={2} alignItems="center" key={item.id} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={3}>
                <TextField label="Telephone Number" fullWidth {...register(`telephones.${index}.number` as const)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Billing Telephone Number" fullWidth {...register(`telephones.${index}.billingNumber` as const)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Account Number / PIN" fullWidth {...register(`telephones.${index}.accountPin` as const)} />
              </Grid>
              <Grid item xs={12} sm={1}>
                <Button variant="outlined" color="error" onClick={() => remove(index)} sx={{ mt: 1 }}>
                  X
                </Button>
              </Grid>
            </Grid>
          ))}

          <Button variant="contained" onClick={() => append({ number: "", billingNumber: "", accountPin: "" })} sx={{ mb: 2 }} disabled={isMaxLimit}>
            + Add Another Number
          </Button>

          {isMaxLimit && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You have reached the maximum of 10 telephone entries allowed for this form.
            </Alert>
          )}

          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle1" mb={2}>
            Customer Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Current Service Provider" fullWidth {...register("provider")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Customer Name" fullWidth {...register("customerName")} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Street Address" fullWidth {...register("address")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="City, State, Zip" fullWidth {...register("cityStateZip")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Contact Telephone" fullWidth {...register("contactPhone")} />
            </Grid>
          </Grid>

          <Divider sx={{ mt: 3, mb: 3 }} />

          <Typography variant="subtitle1" mb={1}>
            Authorized Signature
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Authorized Signatory (printed name)" fullWidth {...register("signatoryName")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Title" fullWidth {...register("title")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Signature" fullWidth {...register("signature")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Date Signed" fullWidth {...register("dateSigned")} />
            </Grid>
          </Grid>

          <Box mt={4}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Generating Preview..." : "Generate Preview"}
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 1 && pdfUrl && (
        <Box sx={{ width: "100%", position: "relative" }}>
          <Typography variant="h6" mb={2}>
            Preview your completed form
          </Typography>

          <Box sx={{ width: "100%", height: "80vh" }}>
            <iframe
              src={pdfUrl}
              title="Porting Preview"
              style={{
                border: "none",
                width: "100%",
                height: "100%",
                display: "block",
              }}
            />
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              borderTop: "1px solid var(--fx-surface-borderstrong, #CBD5E1)",
              p: 2,
              display: "flex",
              gap: 2,
              justifyContent: "center",
              zIndex: 1200,
            }}
          >
            <Button variant="outlined" color="error" onClick={() => window.location.reload()} sx={{ width: 200 }}>
              Cancel
            </Button>

            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit(handleSubmitRecord)}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{ width: 200 }}
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box mt={6} textAlign="center">
          <Typography variant="h5" color="success.main" fontWeight="bold">
            Porting Form Submitted Successfully
          </Typography>
          <Typography variant="body1" mt={1}>
            Your form has been saved. You can close this page.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
